import { Box, Button, Input, Sheet, Tooltip, Typography } from "@mui/joy";
import moment from "moment";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { setFormOpen } from "../../reducers/rhapsody";
import { ExportDialog } from "../servicesList/exportDialog";
import { SegmentedControl } from "../servicesList/segmentedControl";
import { usePro } from "../usePro/usePro";
import {
  ActivityChart,
  cardSx,
  formatHours,
  Leaderboard,
  ProLock,
  SectionCard,
  StatCard,
  TypeBreakdown,
  VenueList,
} from "./insightsKit";
import { SAMPLE_INSIGHTS } from "./sampleInsights";
import { ServicesDialog } from "./servicesDialog";
import {
  DateRange,
  MonthBucket,
  OrgStat,
  ResolvedService,
  TypeStat,
  useInsights,
  VenueStat,
} from "./useInsights";

type Drill = { title: string; subtitle: string; items: ResolvedService[] };

/**
 * Short "{n} services · {hours}" summary line for a drill-down dialog.
 *
 * @param count number of services
 * @param hours total hours
 * @returns {string} the summary
 */
function summary(count: number, hours: number): string {
  return `${count} service${count === 1 ? "" : "s"} · ${formatHours(hours)}`;
}

const PRESETS: { label: string; from: string; to: string }[] = [
  {
    label: "This year",
    from: moment().startOf("year").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  },
  {
    label: "Last 12 months",
    from: moment().subtract(12, "months").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  },
  {
    label: "Last 3 years",
    from: moment().subtract(3, "years").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
  },
  { label: "All time", from: "", to: "" },
];

/**
 * Insights page — a musician's performance dashboard: services played, who
 * books them the most, and the hours they've put in, over an adjustable date
 * range. A Rhapsody Pro feature; non-Pro members see blurred placeholders.
 *
 * @returns {ReactElement} the Insights page
 */
export function InsightsPage() {
  const pro = usePro();
  const locked = pro !== true;
  const dispatch = useDispatch();

  // Default to a full, recent window.
  const [range, setRange] = useState<DateRange>({
    from: PRESETS[1].from,
    to: PRESETS[1].to,
  });
  const [metric, setMetric] = useState<"services" | "hours">("services");
  const [drill, setDrill] = useState<Drill | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const real = useInsights(range);
  const data = locked ? SAMPLE_INSIGHTS : real;

  const activePreset = useMemo(
    () =>
      PRESETS.find((p) => p.from === range.from && p.to === range.to)?.label,
    [range]
  );

  const openUpgrade = () =>
    dispatch(setFormOpen({ formID: "upgradeOpen", isOpen: true }));

  // Drill-down: clicking a widget item lists the matching services.
  const onOrgClick = (org: OrgStat) =>
    setDrill({
      title: org.name,
      subtitle: summary(org.count, org.hours),
      items: data.services.filter((s) => s.contractor?.id === org.id),
    });
  const onVenueClick = (venue: VenueStat) =>
    setDrill({
      title: venue.name,
      subtitle: [venue.location, summary(venue.count, venue.hours)]
        .filter(Boolean)
        .join(" · "),
      items: data.services.filter((s) => s.venue?.id === venue.id),
    });
  const onTypeClick = (type: TypeStat) =>
    setDrill({
      title: type.title,
      subtitle: summary(type.count, type.hours),
      items: data.services.filter(
        (s) => s.gig.sessionTypeID === type.sessionTypeID
      ),
    });
  const onMonthClick = (month: MonthBucket) =>
    setDrill({
      title: `${month.label} ${month.year}`,
      subtitle: summary(month.count, month.hours),
      items: data.services.filter(
        (s) => s.gig.from(s.venue?.tzName).format("YYYY-MM") === month.key
      ),
    });

  const onExportClick = () => {
    if (locked) openUpgrade();
    else setExportOpen(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        width: "100vw",
        position: "relative",
        maxWidth: 1000,
        margin: "0 auto",
        p: { xs: 2, md: 3 },
        pt: { xs: 2, md: 4 },
        gap: 2,
      }}
    >
      <Helmet>
        <title>Rhapsody | Insights</title>
      </Helmet>

      {/* Header (always crisp) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            level="body2"
            sx={{
              color: "var(--mui-palette-text-secondary)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              fontSize: "0.75rem",
            }}
          >
            Insights
          </Typography>
          <Typography
            sx={{
              fontSize: "1.75rem",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Your year in music
          </Typography>
          <Typography
            level="body2"
            sx={{ color: "var(--mui-palette-text-secondary)", mt: 0.25 }}
          >
            A look at the work you've performed — services, organizations and
            hours.
          </Typography>
        </Box>
        <Tooltip size="sm" arrow title="Export as Excel or a work-history PDF">
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onExportClick}
            startDecorator={<i className="fa-solid fa-arrow-down-to-line"></i>}
            endDecorator={
              locked ? (
                <Typography
                  level="body5"
                  variant="outlined"
                  color="info"
                  sx={{ borderRadius: "4px", px: 0.5 }}
                >
                  PRO
                </Typography>
              ) : undefined
            }
            sx={{ borderRadius: "10px", fontWeight: 600, flexShrink: 0 }}
          >
            Export
          </Button>
        </Tooltip>
      </Box>

      {/* Gated region */}
      <Box sx={{ position: "relative" }}>
        {locked ? <ProLock onUnlock={openUpgrade} /> : null}

        <Box
          aria-hidden={locked}
          sx={
            locked
              ? {
                  filter: "blur(7px)",
                  pointerEvents: "none",
                  userSelect: "none",
                  maxHeight: 620,
                  overflow: "hidden",
                  WebkitMaskImage:
                    "linear-gradient(180deg, #000 60%, transparent 100%)",
                  maskImage:
                    "linear-gradient(180deg, #000 60%, transparent 100%)",
                }
              : undefined
          }
        >
          <InsightsBody
            data={data}
            range={range}
            setRange={setRange}
            activePreset={activePreset}
            metric={metric}
            setMetric={setMetric}
            locked={locked}
            onOrgClick={locked ? undefined : onOrgClick}
            onVenueClick={locked ? undefined : onVenueClick}
            onTypeClick={locked ? undefined : onTypeClick}
            onMonthClick={locked ? undefined : onMonthClick}
          />
        </Box>
      </Box>

      <ServicesDialog
        open={!!drill}
        title={drill?.title ?? ""}
        subtitle={drill?.subtitle}
        items={drill?.items ?? []}
        onClose={() => setDrill(null)}
      />
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        items={data.allServices}
      />
    </Box>
  );
}

/**
 * The metrics layout: date-range control, headline stats, activity chart,
 * organization leaderboard and session-type mix. Rendered for both the real
 * (Pro) data and the blurred sample data.
 *
 * @param root0 props
 * @param root0.data the insights to render
 * @param root0.range the active date range
 * @param root0.setRange updates the date range
 * @param root0.activePreset label of the active preset, if any
 * @param root0.metric the active leaderboard metric
 * @param root0.setMetric updates the leaderboard metric
 * @param root0.locked whether the page is Pro-locked
 * @param root0.onOrgClick organization drill-down handler
 * @param root0.onVenueClick venue drill-down handler
 * @param root0.onTypeClick session-type drill-down handler
 * @param root0.onMonthClick month drill-down handler
 * @returns {ReactElement} InsightsBody
 */
function InsightsBody({
  data,
  range,
  setRange,
  activePreset,
  metric,
  setMetric,
  locked,
  onOrgClick,
  onVenueClick,
  onTypeClick,
  onMonthClick,
}: {
  data: ReturnType<typeof useInsights>;
  range: DateRange;
  setRange: (r: DateRange) => void;
  activePreset?: string;
  metric: "services" | "hours";
  setMetric: (m: "services" | "hours") => void;
  locked: boolean;
  onOrgClick?: (org: OrgStat) => void;
  onVenueClick?: (venue: VenueStat) => void;
  onTypeClick?: (type: TypeStat) => void;
  onMonthClick?: (month: MonthBucket) => void;
}) {
  const periodLabel = activePreset ?? "Selected period";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Date range */}
      <Sheet
        sx={{
          ...cardSx,
          p: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {PRESETS.map((p) => {
            const active = range.from === p.from && range.to === p.to;
            return (
              <Typography
                key={p.label}
                onClick={() => setRange({ from: p.from, to: p.to })}
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "999px",
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "all 0.15s ease",
                  color: active ? "#fff" : "var(--mui-palette-text-secondary)",
                  background: active ? "#0A84FF" : "rgba(120, 120, 128, 0.12)",
                }}
              >
                {p.label}
              </Typography>
            );
          })}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Input
            size="sm"
            type="date"
            value={range.from}
            onChange={(e) => setRange({ ...range, from: e.target.value })}
            startDecorator={
              <Typography level="body4" sx={{ fontWeight: 600 }}>
                From
              </Typography>
            }
            sx={{ borderRadius: "10px" }}
          />
          <Input
            size="sm"
            type="date"
            value={range.to}
            onChange={(e) => setRange({ ...range, to: e.target.value })}
            startDecorator={
              <Typography level="body4" sx={{ fontWeight: 600 }}>
                To
              </Typography>
            }
            sx={{ borderRadius: "10px" }}
          />
        </Box>
      </Sheet>

      {/* Loading / empty states only matter for the real (unlocked) data */}
      {!locked && data.isLoading ? (
        <LoadingState />
      ) : !locked && !data.hasAnyData ? (
        <EmptyState
          icon="fa-regular fa-chart-line"
          title="No insights yet"
          caption="Once you've played some services, your stats will appear here — counts, your top organizations and hours performed."
        />
      ) : !locked && data.servicesCount === 0 ? (
        <EmptyState
          icon="fa-regular fa-calendar"
          title="Nothing in this period"
          caption="You have no performed services in the selected range. Try widening the dates or pick “All time”."
        />
      ) : (
        <>
          {/* Headline stats */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
              gap: 1.5,
            }}
          >
            <StatCard
              icon="fa-solid fa-music"
              color="#0A84FF"
              value={data.servicesCount}
              label="Services performed"
              hint={`${
                Math.round(data.avgPerMonth * 10) / 10
              } / month on average`}
            />
            <StatCard
              icon="fa-solid fa-clock"
              color="#FF9500"
              value={formatHours(data.totalHours)}
              label="Hours played"
              hint="Total time on stage"
            />
            <StatCard
              icon="fa-solid fa-globe"
              color="#34C759"
              value={data.organizationsCount}
              label="Organizations"
              hint={data.topOrg ? `Top: ${data.topOrg.name}` : undefined}
            />
            <StatCard
              icon="fa-solid fa-location-dot"
              color="#FF2D55"
              value={data.venuesCount}
              label="Venues"
              hint="Places you've performed"
            />
          </Box>

          {/* Activity over time */}
          <SectionCard
            icon="fa-solid fa-chart-column"
            iconColor="#0A84FF"
            title="Activity"
            subtitle={
              data.busiestMonth
                ? `Busiest: ${data.busiestMonth.label} ${data.busiestMonth.year} · ${periodLabel}`
                : periodLabel
            }
          >
            <ActivityChart
              months={data.months}
              organizations={data.organizations}
              onSelectMonth={onMonthClick}
            />
          </SectionCard>

          {/* Leaderboards: organizations + venues */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              alignItems: "start",
            }}
          >
            <SectionCard
              icon="fa-solid fa-trophy"
              iconColor="#FF9500"
              title="Top organizations"
              subtitle="Who books you the most"
              action={
                <SegmentedControl
                  options={["Services", "Hours"]}
                  value={metric === "services" ? 0 : 1}
                  onChange={(i) => setMetric(i === 0 ? "services" : "hours")}
                />
              }
            >
              {data.organizations.length ? (
                <Leaderboard
                  organizations={data.organizations}
                  metric={metric}
                  onSelect={onOrgClick}
                />
              ) : (
                <InlineEmpty caption="No organizations in this period." />
              )}
            </SectionCard>

            <SectionCard
              icon="fa-solid fa-location-dot"
              iconColor="#FF2D55"
              title="Top venues"
              subtitle="Where you perform most"
            >
              {data.venues.length ? (
                <VenueList venues={data.venues} onSelect={onVenueClick} />
              ) : (
                <InlineEmpty caption="No venues in this period." />
              )}
            </SectionCard>
          </Box>

          {/* Service mix */}
          <SectionCard
            icon="fa-solid fa-guitar"
            iconColor="#AF52DE"
            title="Service mix"
            subtitle="By session type"
          >
            {data.types.length ? (
              <TypeBreakdown types={data.types} onSelect={onTypeClick} />
            ) : (
              <InlineEmpty caption="No services in this period." />
            )}
          </SectionCard>
        </>
      )}
    </Box>
  );
}

/**
 * Shimmer skeleton while the real insights load.
 *
 * @returns {ReactElement} LoadingState
 */
function LoadingState() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
          gap: 1.5,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={{ ...shimmerSx, height: 116 }} />
        ))}
      </Box>
      <Box sx={{ ...shimmerSx, height: 200 }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
          gap: 2,
        }}
      >
        <Box sx={{ ...shimmerSx, height: 240 }} />
        <Box sx={{ ...shimmerSx, height: 240 }} />
      </Box>
    </Box>
  );
}

const shimmerSx = {
  borderRadius: "18px",
  background:
    "linear-gradient(90deg, rgba(120,120,128,0.07) 25%, rgba(120,120,128,0.14) 50%, rgba(120,120,128,0.07) 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.2s linear infinite",
} as const;

/**
 * Centered empty state for the whole metrics area.
 *
 * @param root0 props
 * @param root0.icon FontAwesome class
 * @param root0.title heading
 * @param root0.caption supporting copy
 * @returns {ReactElement} EmptyState
 */
function EmptyState({
  icon,
  title,
  caption,
}: {
  icon: string;
  title: string;
  caption: string;
}) {
  return (
    <Sheet
      sx={{
        ...cardSx,
        py: 8,
        px: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "rgba(120, 120, 128, 0.09)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
          fontSize: 24,
          color: "var(--mui-palette-text-tertiary)",
        }}
      >
        <i className={icon}></i>
      </Box>
      <Typography level="body1" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography
        level="body2"
        sx={{ color: "var(--mui-palette-text-secondary)", maxWidth: 360 }}
      >
        {caption}
      </Typography>
    </Sheet>
  );
}

/**
 * Small inline empty placeholder used inside a section card.
 *
 * @param root0 props
 * @param root0.caption supporting copy
 * @returns {ReactElement} InlineEmpty
 */
function InlineEmpty({ caption }: { caption: string }) {
  return (
    <Typography
      level="body3"
      sx={{
        color: "var(--mui-palette-text-tertiary)",
        py: 2,
        textAlign: "center",
      }}
    >
      {caption}
    </Typography>
  );
}
